import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();
  /** @type {number | null} Width to lock after close, captured before layout collapses. */
  #pendingWidthLock = null;

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('toggle', this.#handleToggle, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * When this accordion opens, close all sibling accordions in the same group.
   * When this accordion closes and no siblings are open, lock the accordion's
   * inline size so the parent fit-content column doesn't collapse.
   */
  #handleToggle = () => {
    const parent = this.closest('.accordion');
    if (!parent) return;

    if (!this.details.open) {
      // Just closed — apply the pre-collapse width lock if no other item is open.
      const anyOpen = [...parent.querySelectorAll('accordion-custom')].some(
        (ac) => ac !== this && ac.querySelector('details')?.open
      );
      if (!anyOpen && this.#pendingWidthLock !== null) {
        parent.style.minInlineSize = `${this.#pendingWidthLock}px`;
      }
      this.#pendingWidthLock = null;
      return;
    }

    // Just opened — release any locked width, then close siblings.
    this.#pendingWidthLock = null;
    parent.style.removeProperty('min-inline-size');

    parent.querySelectorAll('accordion-custom').forEach((sibling) => {
      if (sibling === this) return;
      const details = sibling.querySelector('details');
      if (details instanceof HTMLDetailsElement && details.open) {
        details.open = false;
      }
    });
  };

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    // If this item is open and about to close, capture the accordion width NOW —
    // before the toggle removes ::details-content from layout.
    if (this.details.open) {
      const parent = this.closest('.accordion');
      if (parent) this.#pendingWidthLock = parent.offsetWidth;
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
